/**
 * The ONE subject + grade catalog for student-facing pickers (UX-13).
 *
 * Extracted verbatim from LearningPaths.js so SettingsDialog's new
 * learning-preferences editor cannot drift from the path-creation grid —
 * before this, Onboarding carried its own 4-subject list "kept in sync"
 * by comment alone, and Settings had no list at all.
 *
 * `label` is what StudentProfile.preferred_subjects stores (an opaque
 * display preference — the backend never matches it against an enum);
 * `value` is the lowercase string POSTed to /api/v1/learning-paths.
 *
 * GRADE CONVENTION: StudentProfile.current_grade_level is an int with
 * K = 0 … 12. Pre-UX-13 rows may hold the old onboarding band-top values
 * (2/5/8/12), which read back as those exact grades — no migration needed.
 */

export const LANGUAGE_GRADES = ['K','1','2','3','4','5','6','7','8','9','10','11','12'];

// POST-AIQ-114 (2026-05-13): the World Languages aggregate card was split
// into 8 per-language cards. Each language is a top-level subject — picks
// its own grade, creates its own LearningPath row, and routes to a
// language-specific AI prompt block on the backend.
//
// ALL_SUBJECTS is the whole catalog; the pickers render SUBJECTS (below),
// which hides what has no established curriculum behind it yet.
export const ALL_SUBJECTS = [
  { label: 'Mathematics', value: 'mathematics', icon: '\u{1F4D0}', description: 'Numbers, algebra, geometry, calculus', grades: ['K','1','2','3','4','5','6','7','8','9','10','11','12'] },
  { label: 'English Language Arts', value: 'english', icon: '\u{1F4D6}', description: 'Reading, writing, grammar, literature', grades: ['K','1','2','3','4','5','6','7','8','9','10','11','12'] },
  { label: 'Science', value: 'science', icon: '\u{1F52C}', description: 'Physics, chemistry, biology, earth science', grades: ['K','1','2','3','4','5','6','7','8','9','10','11','12'] },
  { label: 'Social Studies', value: 'social studies', icon: '\u{1F30D}', description: 'History, civics, geography, economics', grades: ['K','1','2','3','4','5','6','7','8','9','10','11','12'] },
  { label: 'Computer Science', value: 'cs', icon: '\u{1F4BB}', description: 'Coding, algorithms, data structures', grades: ['3','4','5','6','7','8','9','10','11','12'] },
  { label: 'Spanish', value: 'spanish', icon: '\u{1F1EA}\u{1F1F8}', description: 'Vocabulary, grammar, conjugation, culture', grades: LANGUAGE_GRADES },
  { label: 'French', value: 'french', icon: '\u{1F1EB}\u{1F1F7}', description: 'Romance language; Francophone culture', grades: LANGUAGE_GRADES },
  { label: 'Mandarin', value: 'mandarin', icon: '\u{1F1E8}\u{1F1F3}', description: 'Tones, characters, pinyin', grades: LANGUAGE_GRADES },
  { label: 'German', value: 'german', icon: '\u{1F1E9}\u{1F1EA}', description: 'Cases, compound words, Germanic roots', grades: LANGUAGE_GRADES },
  { label: 'Italian', value: 'italian', icon: '\u{1F1EE}\u{1F1F9}', description: 'Romance language; pronunciation-friendly', grades: LANGUAGE_GRADES },
  { label: 'Latin', value: 'latin', icon: '\u{1F3DB}\u{FE0F}', description: 'Classical; grammar foundation; SAT vocab', grades: LANGUAGE_GRADES },
  { label: 'Japanese', value: 'japanese', icon: '\u{1F1EF}\u{1F1F5}', description: 'Hiragana, katakana, kanji', grades: LANGUAGE_GRADES },
  { label: 'ASL', value: 'asl', icon: '\u{1F91F}', description: 'American Sign Language; Deaf culture', grades: LANGUAGE_GRADES },
  { label: 'Financial Literacy', value: 'financial literacy', icon: '\u{1F4B0}', description: 'Budgeting, investing, economics', grades: ['6','7','8','9','10','11','12'] },
  { label: 'Health & Wellness', value: 'health', icon: '\u{2764}\u{FE0F}', description: 'Nutrition, mental health, safety', grades: ['K','1','2','3','4','5','6','7','8','9','10','11','12'] },
];

// 2026-10-24 (the owner, for the test deploy): a picker offers a subject only
// when an ESTABLISHED, standards-tied curriculum backs it — the five whose
// taxonomy is aligned to CCSS-M / CCSS-ELA / NGSS / the C3 Framework / CSTA
// (backend/app/routes/_curriculum.py). The eight languages, Financial Literacy
// and Health & Wellness have a hand-written taxonomy but no standards data
// behind them: they stay in ALL_SUBJECTS (an existing path in one of them keeps
// working — the backend still serves every subject) but are hidden from every
// picker until a curriculum is in. REACT_APP_SHOW_ALL_SUBJECTS=1 at build time
// offers them again (substituted in vite.config.js's define block).
export const HIDDEN_FOR_NOW = new Set([
  'spanish', 'french', 'mandarin', 'german', 'italian', 'latin', 'japanese', 'asl',
  'financial literacy', 'health',
]);

export const CORE_SUBJECT_VALUES = ['mathematics', 'english', 'science', 'social studies', 'cs'];

const showAll = process.env.REACT_APP_SHOW_ALL_SUBJECTS === '1';

/** The subjects every picker offers: the core five, unless the build asks for all. */
export const SUBJECTS = showAll
  ? ALL_SUBJECTS
  : ALL_SUBJECTS.filter((s) => !HIDDEN_FOR_NOW.has(s.value));

export const GRADE_DISPLAY = {
  'K': 'K', '1': '1st', '2': '2nd', '3': '3rd',
  '4': '4th', '5': '5th', '6': '6th', '7': '7th',
  '8': '8th', '9': '9th', '10': '10th', '11': '11th', '12': '12th',
};

// Exact-grade choices for Onboarding + Settings (UX-13 A2 — the old
// onboarding grade BANDS stored a band-top int nothing could prefill from).
//
// TWO label forms, deliberately (adversarial review of #549, H3):
//  - `pillAria` labels a BUTTON whose visible text is `display` — WCAG 2.5.3
//    (Label in Name) requires the visible text to be a substring of the
//    accessible name, so it embeds the display form ("Grade 3rd" ⊇ "3rd";
//    "Kindergarten" ⊇ "K").
//  - `optionLabel` is VISIBLE text for <select> options, where plain
//    "Grade 3" reads naturally and 2.5.3 is satisfied trivially.
export const GRADE_CHOICES = [
  { value: 0, display: 'K', pillAria: 'Kindergarten', optionLabel: 'Kindergarten' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    display: GRADE_DISPLAY[String(i + 1)],
    pillAria: `Grade ${GRADE_DISPLAY[String(i + 1)]}`,
    optionLabel: `Grade ${i + 1}`,
  })),
];

/** stored int (K=0 … 12) → the grade-picker string ('K' | '1' … '12'), or
 *  null when unset/out of range. `0` is int-falsy — always compare against
 *  null/undefined, never truthiness. */
export function gradeIntToPicker(gradeLevel) {
  if (gradeLevel === null || gradeLevel === undefined) return null;
  if (gradeLevel === 0) return 'K';
  if (gradeLevel >= 1 && gradeLevel <= 12) return String(gradeLevel);
  return null;
}
